import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getUsers } from "../redux/userSpeaker/userSpeakerSlice";
import { assignRole, getRoles } from "../redux/role/roleSlice";
import { toast } from "react-toastify";
import Select from "react-select";
import useDebounce from "../hook/useDebounce";
import Avatar from "./Avtar/Avatar";
import { truncateText } from "../helper/helper";
import PaginationHelper from "../helper/PaginationHelper";

const AssignRoleLayer = () => {
    const dispatch = useDispatch();

    const { users = [], total = 0, totalPages = 1 } =
        useSelector((state) => state.speaker);

    const { roles = [] } = useSelector((state) => state.role);

    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [entries, setEntries] = useState(50);
    const [currentPage, setCurrentPage] = useState(1);
    const [confirmData, setConfirmData] = useState(null);
    const debouncedSearch = useDebounce(search, 1000);

    /* FETCH USERS */
    useEffect(() => {
        dispatch(
            getUsers({
                page: currentPage,
                limit: entries,
                search: debouncedSearch,
                status: statusFilter,
            })
        );
    }, [dispatch, currentPage, entries, debouncedSearch, statusFilter]);

    /* FETCH ROLES */
    useEffect(() => {
        dispatch(getRoles());
    }, [dispatch]);

    /* ASSIGN ROLE */
    const handleAssignRole = async (user, role) => {
        const confirm = window.confirm(
            `Are you sure you want to assign "${role.name}" role to ${user.fullName}?`
        );

        if (!confirm) return;

        try {
            await dispatch(
                assignRole({
                    userId: user.id,
                    roleId: role.id,
                })
            ).unwrap();

            toast.success("Role updated successfully");

            dispatch(
                getUsers({
                    page: currentPage,
                    limit: entries,
                    search: debouncedSearch,
                    status: statusFilter,
                })
            );
        } catch (err) {
            toast.error(err?.message || "Failed to update role");
        }
    };

    return (
        <div className="card h-100 p-0 radius-12">

            {/* HEADER */}
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">

                <div className="d-flex align-items-center flex-wrap gap-3">

                    <span className="text-md fw-medium text-secondary-light">
                        Show
                    </span>

                    <select
                        className="form-select form-select-sm w-auto"
                        value={entries}
                        onChange={(e) => {
                            setEntries(Number(e.target.value));
                            setCurrentPage(1);
                        }}
                    >
                        <option value="50">50</option>
                        <option value="100">100</option>
                        <option value="150">150</option>
                        <option value="200">200</option>
                    </select>

                    {/* SEARCH */}
                    <input
                        type="text"
                        className="form-control w-auto"
                        placeholder="Search"
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setCurrentPage(1);
                        }}
                    />

                    {/* STATUS */}
                    {/* <select
                        className="form-select form-select-sm w-auto"
                        value={statusFilter}
                        onChange={(e) => {
                            setStatusFilter(e.target.value);
                            setCurrentPage(1);
                        }}
                    >
                        <option value="">All</option>
                        <option value="true">Active</option>
                        <option value="false">Inactive</option>
                    </select> */}

                </div>
            </div>

            {/* TABLE */}
            <div className="card-body p-12">
                <div className="table-responsive">

                    <table className="table bordered-table">

                        <thead>
                            <tr>
                                <th>S.L</th>
                                <th>Name</th>
                                <th>Email</th> {/* NEW COLUMN */}
                                <th>Current Role</th>
                                <th className="text-center">Assign Role</th>
                            </tr>
                        </thead>

                        <tbody>
                            {users.map((user, index) => {

                                const roleOptions = roles.map((role) => ({
                                    value: role.id,
                                    label: role.name
                                }));

                                return (
                                    <tr key={user.id}>

                                        {/* INDEX */}
                                        <td>
                                            {(currentPage - 1) * entries + index + 1}
                                        </td>

                                        {/* NAME */}
                                        <td>
                                            <div className="d-flex align-items-center gap-2">

                                                <Avatar
                                                    name={user.fullName}
                                                    image={user.profilePicture}
                                                    uniqueKey={user.id}
                                                    size={28}
                                                />

                                                <span>
                                                    {truncateText(user.fullName, 10)}
                                                </span>

                                            </div>
                                        </td>

                                        {/* EMAIL */}
                                        <td>{user.email}</td>

                                        {/* CURRENT ROLE */}
                                        <td>{user?.Role?.name || "No Role"}</td>

                                        {/* ASSIGN ROLE */}
                                        <td style={{ minWidth: "220px" }}>

                                            <Select
                                                options={roleOptions}
                                                menuPortalTarget={document.body}
                                                menuPosition="fixed"

                                                value={
                                                    roleOptions.find(
                                                        (option) => option.value === user?.Role?.id
                                                    ) || null
                                                }

                                                onChange={(selected) => {
                                                    if (!selected) return;

                                                    const role = roles.find((r) => r.id === selected.value);
                                                    setConfirmData({ user, role });
                                                }}

                                                isClearable

                                                placeholder="Assign Role"

                                                classNamePrefix="react-select"

                                                styles={{
                                                    control: (base) => ({
                                                        ...base,
                                                        minHeight: "42px",
                                                        borderRadius: "8px",
                                                        borderColor: "#d1d5db",
                                                    }),

                                                    singleValue: (base) => ({
                                                        ...base,
                                                        backgroundColor: "#87d64c",
                                                        color: "#fff",
                                                        padding: "4px 10px",
                                                        borderRadius: "6px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        gap: "6px",
                                                    }),

                                                    valueContainer: (base) => ({
                                                        ...base,
                                                        padding: "2px 8px",
                                                    }),

                                                    clearIndicator: (base) => ({
                                                        ...base,
                                                        color: "#fff",
                                                        cursor: "pointer",
                                                    }),
                                                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                                                }}
                                            />

                                        </td>

                                    </tr>
                                );
                            })}
                        </tbody>

                    </table>
                </div>
                {confirmData && (
                    <div
                        className="modal fade show d-block"
                        tabIndex={-1}
                        style={{ background: "rgba(0,0,0,0.4)" }} // backdrop
                    >
                        <div className="modal-dialog modal-dialog-centered">

                            <div className="modal-content radius-16 bg-base">

                                {/* HEADER */}
                                <div className="modal-header py-16 px-24 border border-top-0 border-start-0 border-end-0">
                                    <h5 className="modal-title fs-5">
                                        Confirm Role Assignment
                                    </h5>

                                    <button
                                        className="btn-close"
                                        onClick={() => setConfirmData(null)}
                                    />
                                </div>

                                {/* BODY */}
                                <div className="modal-body p-24 text-center">

                                    <div className="mb-16">
                                        <Icon
                                            icon="mdi:account-switch"
                                            className="text-primary-600"
                                            width="48"
                                        />
                                    </div>

                                    <p className="text-md text-secondary-light mb-8">
                                        You are about to assign
                                    </p>

                                    <h6 className="fw-semibold mb-8">
                                        {confirmData.role.name}
                                    </h6>

                                    <p className="text-sm text-secondary-light">
                                        to user
                                    </p>

                                    <h6 className="fw-semibold">
                                        {confirmData.user.fullName}
                                    </h6>

                                </div>

                                {/* FOOTER */}
                                <div className="d-flex align-items-center justify-content-center gap-3 pb-24">

                                    <button
                                        className="border border-danger-600 bg-hover-danger-200 text-danger-600 text-md px-40 py-11 radius-8"
                                        onClick={() => setConfirmData(null)}
                                    >
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary border border-primary-600 text-md px-48 py-12 radius-8"
                                        onClick={async () => {
                                            try {
                                                await dispatch(
                                                    assignRole({
                                                        userId: confirmData.user.id,
                                                        roleId: confirmData.role.id,
                                                    })
                                                ).unwrap();

                                                toast.success("Role updated successfully");

                                                setConfirmData(null);

                                                dispatch(
                                                    getUsers({
                                                        page: currentPage,
                                                        limit: entries,
                                                        search: debouncedSearch,
                                                        status: statusFilter,
                                                    })
                                                );

                                            } catch (err) {
                                                toast.error("Failed to update role");
                                            }
                                        }}
                                    >
                                        Confirm
                                    </button>

                                </div>

                            </div>

                        </div>
                    </div>
                )}
                {/* PAGINATION */}
                <PaginationHelper
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    totalItems={total}
                    pageSize={entries}
                />
            </div>
        </div>
    );
};

export default AssignRoleLayer;